import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  Users,
  User as UserIcon,
  Plus,
  CreditCard,
  Banknote,
  Send,
  Sparkles,
  AlertTriangle,
  ShieldCheck,
  FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import BrandMark from "@/components/BrandMark";
import DisclaimerBanner from "@/components/DisclaimerBanner";

const PLAN_ICONS = { personal: UserIcon, family: Users, extra_call: Plus };

const METHOD_ICONS = {
  stripe: CreditCard,
  western_union: Banknote,
  money_gram: Banknote,
  remitly: Send,
};

const NO_BALANCE_MESSAGES = {
  no_balance_detention:
    "Se te terminaron las llamadas de detención. Compra un plan o una llamada adicional para que la IA siga ayudándote con el formulario I-589.",
  no_balance_emergency:
    "Se te terminaron las llamadas de emergencia. Compra un plan o una llamada adicional para volver a llamar a la IA.",
};
const NO_BALANCE_FALLBACK =
  "Tu plan se agotó. Compra un plan o llamada adicional para seguir usando el servicio.";

const PREVENTIVE_SCOPE = {
  personal: "Para 1 persona",
  family: "Hasta 4 personas",
};

export default function Precios() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const reason = searchParams.get("reason") || "";
  const noBalanceReason = reason.startsWith("no_balance") ? reason : null;
  const isTimeExpired = reason === "time_expired";
  const [data, setData] = useState({ plans: [], methods: [], beneficiary: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/plans").then((r) => {
      setData(r.data);
      setLoading(false);
    });
  }, []);

  const visiblePlans = useMemo(
    () => data.plans.filter((p) => p.id !== "extra_call"),
    [data.plans]
  );

  const selectPlan = (planId) => {
    if (!user) {
      navigate(`/auth?next=/pagar?plan=${planId}`);
    } else {
      navigate(`/pagar?plan=${planId}`);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 pb-28">
      <header className="max-w-5xl mx-auto w-full px-5 pt-6 flex items-center justify-between">
        <button
          onClick={() => navigate(user ? "/dashboard" : "/")}
          className="flex items-center gap-2 text-sm font-semibold text-zinc-600 hover:text-zinc-900"
          data-testid="precios-back"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver
        </button>
        <BrandMark size="sm" />
      </header>

      <div className="max-w-5xl mx-auto w-full px-5 pt-10">
        {noBalanceReason && (
          <div
            data-testid="precios-no-balance-banner"
            className="mb-6 rounded-2xl border-2 border-amber-400 bg-amber-50 p-4 sm:p-5 flex items-start gap-3"
          >
            <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-amber-950" strokeWidth={2.5} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-black text-amber-900 mb-0.5">
                Tu plan no tiene llamadas restantes
              </p>
              <p className="text-xs text-zinc-700 leading-snug">
                {NO_BALANCE_MESSAGES[noBalanceReason] || NO_BALANCE_FALLBACK}
              </p>
            </div>
          </div>
        )}

        {isTimeExpired && (
          <div
            data-testid="precios-time-expired-banner"
            className="mb-6 rounded-2xl border-2 border-red-400 bg-red-50 p-4 sm:p-5"
          >
            <div className="flex items-start gap-3 mb-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-red-600 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-white" strokeWidth={2.5} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-black text-red-900 mb-0.5">
                  Tu llamada de 20 minutos terminó
                </p>
                <p className="text-xs text-zinc-700 leading-snug">
                  Compra <span className="font-bold">+20 minutos por $8 USD</span> para continuar exactamente donde te quedaste.
                </p>
              </div>
            </div>
            <Button
              data-testid="precios-buy-extra-time"
              onClick={() => selectPlan("extra_time")}
              className="w-full h-12 bg-red-600 hover:bg-red-700 text-white font-black rounded-xl"
            >
              Comprar +20 minutos por $8 ahora
            </Button>
          </div>
        )}

        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-widest font-black text-red-600 mb-2">
            Pago único · Sin suscripciones · Sin renovaciones automáticas
          </p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-zinc-900 leading-tight">
            Elige tu protección
          </h1>
          <p className="text-base text-zinc-700 mt-3 max-w-xl mx-auto leading-relaxed">
            Cada plan incluye llamadas <span className="font-bold">numeradas</span> con la IA. Cada persona usa la suya UNA sola vez por escenario para garantizar que el servicio llegue a quien lo necesita.
          </p>
        </div>

        {/* Plans grid */}
        <div className="grid md:grid-cols-2 gap-5 mb-12">
          {visiblePlans.map((p) => {
            const Icon = PLAN_ICONS[p.id] || UserIcon;
            return (
              <div
                key={p.id}
                data-testid={`plan-${p.id}`}
                className={`relative bg-white rounded-3xl p-7 border-2 ${
                  p.highlight ? "border-red-600 shadow-[0_20px_50px_rgba(220,38,38,0.15)]" : "border-zinc-200"
                }`}
              >
                {p.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-red-600 text-white text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    Más elegido
                  </div>
                )}

                {/* PLAN DE PROTECCIÓN PREVENTIVA — preventive I-589 branding banner */}
                {PREVENTIVE_SCOPE[p.id] && (
                  <div
                    data-testid={`plan-${p.id}-preventive-banner`}
                    className="relative overflow-hidden mb-5 rounded-2xl bg-gradient-to-br from-indigo-950 via-blue-900 to-indigo-800 p-4 ring-1 ring-amber-400/40"
                  >
                    {/* subtle glow */}
                    <div className="absolute -top-10 -right-10 w-32 h-32 bg-amber-400/20 rounded-full blur-2xl pointer-events-none" />
                    <div className="relative flex items-start gap-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-amber-400 flex items-center justify-center shadow-lg shadow-amber-500/30">
                        <ShieldCheck className="w-5 h-5 text-indigo-950" strokeWidth={2.8} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 mb-1">
                          <p className="text-[10px] uppercase tracking-[0.15em] font-black text-amber-300">
                            Plan de Protección Preventiva
                          </p>
                          <Sparkles className="w-3 h-3 text-amber-300" />
                        </div>
                        <p className="text-sm font-black text-white leading-tight">
                          Prepara tu Formulario <span className="text-amber-300">I-589</span>
                        </p>
                        <p className="text-[11px] font-bold text-blue-100 mt-0.5 leading-snug flex items-center gap-1">
                          <FileText className="w-3 h-3 text-amber-300 flex-shrink-0" />
                          EVITA deportación expedita · {PREVENTIVE_SCOPE[p.id]}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex items-start justify-between mb-4">
                  <div className="w-14 h-14 rounded-2xl bg-zinc-900 flex items-center justify-center">
                    <Icon className="w-7 h-7 text-white" strokeWidth={2.5} />
                  </div>
                  <p className="text-xs uppercase tracking-widest font-black text-zinc-500">
                    {p.tagline}
                  </p>
                </div>
                <h3 className="text-2xl font-black text-zinc-900 mb-1">{p.name}</h3>
                <p className="text-sm text-zinc-600 leading-snug mb-5">{p.description}</p>
                <div className="flex items-baseline gap-1 mb-5">
                  <span className="text-5xl font-black tracking-tighter text-zinc-900">
                    ${p.price_usd}
                  </span>
                  <span className="text-base font-bold text-zinc-500">USD</span>
                </div>

                <ul className="space-y-2.5 mb-6">
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-zinc-800 leading-snug">
                      <span className="font-bold">{p.emergency_calls}</span> llamada{p.emergency_calls > 1 ? "s" : ""} de emergencia (frente a ICE)
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-zinc-800 leading-snug">
                      <span className="font-bold">{p.detention_calls}</span> llamada{p.detention_calls > 1 ? "s" : ""} en centro de detención
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-zinc-800 leading-snug">
                      Hasta <span className="font-bold">{p.members}</span> {p.members > 1 ? "miembros de tu familia" : "persona"}
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-zinc-800 leading-snug">
                      Acceso al centro de recursos, consulados y abogados pro bono
                    </span>
                  </li>
                </ul>

                <Button
                  data-testid={`plan-${p.id}-cta`}
                  onClick={() => selectPlan(p.id)}
                  className={`w-full h-14 text-base font-black rounded-xl ${
                    p.highlight
                      ? "bg-red-600 hover:bg-red-700 text-white shadow-lg"
                      : "bg-zinc-900 hover:bg-zinc-800 text-white"
                  }`}
                >
                  Elegir {p.name}
                </Button>
              </div>
            );
          })}
        </div>

        {/* Extra call addon */}
        {data.plans.find(p => p.id === "extra_call") && (() => {
          const extra = data.plans.find(p => p.id === "extra_call");
          return (
            <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-6 mb-12 flex items-center gap-4 flex-wrap">
              <div className="w-14 h-14 rounded-2xl bg-amber-600 flex items-center justify-center flex-shrink-0">
                <Plus className="w-7 h-7 text-white" strokeWidth={3} />
              </div>
              <div className="flex-1 min-w-[200px]">
                <p className="text-xs uppercase tracking-widest font-black text-amber-700">¿Necesitas más llamadas?</p>
                <h3 className="text-xl font-black text-zinc-900">Llamada adicional · ${extra.price_usd} USD</h3>
                <p className="text-sm text-zinc-700 mt-0.5 leading-snug">{extra.description}</p>
              </div>
              <Button
                data-testid="plan-extra_call-cta"
                onClick={() => selectPlan("extra_call")}
                className="h-12 px-6 font-bold bg-amber-600 hover:bg-amber-700 text-white rounded-xl"
              >
                Comprar
              </Button>
            </div>
          );
        })()}

        {/* Payment methods */}
        <h2 className="text-2xl font-black tracking-tight text-zinc-900 mb-4 text-center">
          Cómo puedes pagar
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
          {data.methods.map((m) => {
            const Icon = METHOD_ICONS[m.id] || CreditCard;
            return (
              <div
                key={m.id}
                data-testid={`method-${m.id}`}
                className="bg-white border-2 border-zinc-200 rounded-xl p-4"
              >
                <Icon className={`w-7 h-7 mb-2 ${m.instant ? "text-emerald-600" : "text-zinc-700"}`} strokeWidth={2.5} />
                <p className="font-bold text-zinc-900 text-sm leading-tight">{m.name}</p>
                <p className="text-xs text-zinc-500 mt-0.5 leading-snug">{m.subtitle}</p>
                {m.instant && (
                  <span className="inline-block mt-2 text-[10px] font-black uppercase text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                    Instantáneo
                  </span>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-center text-xs text-zinc-500 max-w-2xl mx-auto leading-relaxed">
          Para Western Union, MoneyGram y Remitly el pago se confirma manualmente dentro de las 24 horas siguientes a tu depósito. Te avisaremos en tu cuenta apenas se acredite.
        </p>
      </div>

      <DisclaimerBanner />
    </div>
  );
}
